// AF-3.20 Phase 14/22 — Real-Brand Readiness Gate.
//
// This is a CODE-AUDITED CHECKLIST, not a live-polling dashboard. Per
// this stage's own "do not create speculative backend endpoints merely
// to make the frontend look complete" instruction, no new backend route
// was added to compute these statuses live. Every row below is a direct
// finding from this session's Phase 0 source audit, with the exact
// evidence cited -- dated, and intentionally NOT presented as if a
// backend call verified it moments ago. If a genuinely live version of
// this page is wanted later, it needs a real backend readiness route,
// which is out of scope for this stage.
//
// This is a READ-ONLY PILOT gate, not an execution gate -- it says
// nothing about, and does not gate, any write/publish capability,
// because none exists anywhere in this product to gate.
//
// Next audited fix (CORS readiness accuracy): every row EXCEPT CORS
// stays exactly what's described above. CORS is the one exception --
// its real state changes with a single .env edit and a worker restart,
// and a static claim about it goes stale the moment an operator fixes
// (or breaks) it, which is exactly what happened here: this row used to
// describe the AF-3.17 localhost-fallback behavior, which AF-3.20.1 had
// already superseded (fail-closed, never a permissive default) by the
// time this page was next read. Re-dating a static string every time it
// might drift again doesn't fix that; instead, this ONE row is now a
// live read of the existing GET /api/health endpoint's own
// `cors_configured` boolean -- reusing the exact real, already-enforced
// `_CORS_ALLOWED_ORIGINS` list server.py's CORSMiddleware is itself
// built from (never a second parse of CORS_ORIGINS), and never the raw
// origin value(s) themselves -- a boolean only, so a real deployment's
// actual frontend origin(s) are never exposed to the browser. No new
// backend readiness architecture: /health already existed for infra
// monitoring; this reuses it exactly as instructed, adding one field.

import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { healthAPI } from '@/lib/api';
import { deriveCorsCheck, CORS_CHECKING_STATE } from '@/lib/corsReadiness';

const AUDIT_DATE = '2026-08-21 (AF-3.20 Phase 0)';

const STATIC_CHECKS_BEFORE_CORS = [
  {
    id: 'AUTH', label: 'JWT authentication + per-token revocation', status: 'GREEN',
    evidence: 'AF-3.17.1: create_token() embeds a jti; get_current_user() checks db.revoked_tokens before trusting any token; POST /auth/logout revokes exactly one token, tested directly.',
  },
  {
    id: 'RBAC', label: 'Role-based access control', status: 'GREEN',
    evidence: 'AF-3.17.1: 277/277 /intelligence/* routes carry require_role(...); dual-verified (live-app introspection + independent hardcoded oracle), 38 dedicated tests.',
  },
  {
    id: 'RATE_LIMITING', label: 'Rate limiting', status: 'GREEN',
    evidence: 'AF-3.17.1: Mongo-backed RateLimitMiddleware, proven shared across independent process instances (test_limit_state_is_shared_across_independent_limiter_instances), fails open on storage error.',
  },
  {
    id: 'AUDIT', label: 'Audit event logging', status: 'GREEN',
    evidence: 'backend/security_audit.py exists (SecurityAuditEventService, AF-3.17); connect/disconnect/credential-reference events recorded per AF-3.18.',
  },
  {
    id: 'CREDENTIAL_VAULT', label: 'Credential vault operational', status: 'AMBER',
    evidence: 'backend/.env: VAULT_ENCRYPTION_KEY is set (verified this session, value not printed). The currently-running backend worker processes started AFTER this key’s last file-modification timestamp, which by AF-3.17.1’s own documented logic means they likely loaded it — but this was confirmed by timestamp inference, not a live authenticated round-trip test in this session. Verify with a real vault_encrypt/decrypt call before trusting in production.',
  },
  {
    id: 'WORKSPACE_ISOLATION', label: 'Workspace / tenant isolation', status: 'GREEN',
    evidence: 'get_verified_client(client_id, user) requires client["user_id"] == user["id"], generic 404 on mismatch — unchanged and re-tested across every AF-2.x/AF-3.x stage since AF-1R.',
  },
];

const STATIC_CHECKS_AFTER_CORS = [
  {
    id: 'CONNECTOR_PERMISSIONS', label: 'Connector permissions are read-only', status: 'GREEN',
    evidence: 'connectors/shopify.py and connectors/paid_media.py contain zero mutation-shaped calls — every network call is a query/GET-shaped POST (GraphQL query, Insights read) — verified this session by direct source grep.',
  },
  {
    id: 'READ_ONLY_MODE', label: 'No publish/execute capability exists anywhere in the product', static: true, status: 'GREEN',
    evidence: 'Connector action surface is Connect / Health Check / Sync / Disconnect only (DataConnectionsPage.js, AF-3.18). No publish, post, send, launch, or execute capability exists anywhere in this codebase’s frontend or the connector layer — verified this session (Phase 23 static scan, see AF-3.20 report).',
  },
  {
    id: 'DEPENDENCY_SECURITY', label: 'Dependency vulnerability follow-up', status: 'AMBER',
    evidence: 'AF-3.17.1: real pip-audit + npm audit run. 6 production-relevant packages fixed; 15 backend + ~58 frontend (mostly CRA build-tooling) advisories deliberately deferred with documented, prioritized justification — not blind, not resolved.',
  },
];

const TONE = {
  GREEN: { icon: CheckCircle, color: 'text-[var(--success)]', bg: 'bg-[var(--success-bg)]', border: 'border-[var(--success-border)]' },
  AMBER: { icon: WarningCircle, color: 'text-[var(--warning)]', bg: 'bg-[var(--warning-bg)]', border: 'border-[var(--warning-border)]' },
  RED: { icon: XCircle, color: 'text-[var(--error)]', bg: 'bg-[var(--error-bg)]', border: 'border-[var(--error-border)]' },
};

export default function RealBrandReadinessPage() {
  const [corsCheck, setCorsCheck] = useState(CORS_CHECKING_STATE);

  useEffect(() => {
    let cancelled = false;
    healthAPI.check()
      .then((res) => { if (!cancelled) setCorsCheck(deriveCorsCheck(res.data, false)); })
      .catch(() => { if (!cancelled) setCorsCheck(deriveCorsCheck(null, true)); });
    return () => { cancelled = true; };
  }, []);

  const CHECKS = [
    ...STATIC_CHECKS_BEFORE_CORS,
    { id: 'CORS', label: 'CORS_ORIGINS explicitly configured', ...corsCheck },
    ...STATIC_CHECKS_AFTER_CORS,
  ];

  const hasRed = CHECKS.some((c) => c.status === 'RED');
  const hasAmber = CHECKS.some((c) => c.status === 'AMBER');
  const overall = hasRed ? 'RED' : hasAmber ? 'AMBER' : 'GREEN';
  const OverallIcon = TONE[overall].icon;

  return (
    <DashboardLayout>
      <div data-testid="real-brand-readiness-page" className="max-w-3xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[18px] font-bold text-[var(--text-primary)] mb-1">Real-Brand Readiness Gate</h1>
          <p className="text-[11px] text-[var(--text-tertiary)]">
            Code-audited as of {AUDIT_DATE} — not a live poll, except CORS (checked live via GET /api/health). This
            is a READ-ONLY pilot gate; it says nothing about publishing or execution capability, because none exists
            to gate.
          </p>
        </div>

        <div className={`flex items-center gap-3 rounded-[var(--radius-card)] border ${TONE[overall].border} ${TONE[overall].bg} p-4 mb-4`} data-testid="readiness-overall-status">
          <OverallIcon size={22} className={TONE[overall].color} weight="fill" />
          <div>
            <p className={`text-[13px] font-bold ${TONE[overall].color}`}>Overall: {overall}</p>
            <p className="text-[11px] text-[var(--text-secondary)]">
              {overall === 'RED' ? 'Do not connect a real brand until every RED item is resolved.' : overall === 'AMBER' ? 'Real-brand connection is not blocked, but AMBER items should be resolved or explicitly accepted first.' : 'All checks pass.'}
            </p>
          </div>
        </div>

        <div className="space-y-2" data-testid="readiness-checklist">
          {CHECKS.map((c) => {
            const Icon = TONE[c.status].icon;
            return (
              <div key={c.id} className={`rounded-[var(--radius-card)] border ${TONE[c.status].border} bg-[var(--surface)] p-3.5 shadow-[var(--shadow-card)]`} data-testid={`readiness-check-${c.id.toLowerCase()}`}>
                <div className="flex items-start gap-2.5">
                  <Icon size={16} className={`${TONE[c.status].color} mt-0.5 shrink-0`} weight="fill" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[12px] text-[var(--text-primary)] font-semibold">{c.label}</p>
                      <span className={`text-[9px] font-mono uppercase ${TONE[c.status].color}`}>{c.status}</span>
                    </div>
                    <p className="text-[10px] text-[var(--text-tertiary)] mt-1">{c.evidence}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
